import React, { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import {
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Box from '@mui/material/Box'
import { Button, Table } from 'components'
import {
  CrudAction,
  Entity,
  usePageHeader,
  usePagination,
  usePermissions,
} from 'utils/hooks'
import { PlusIcon } from 'assets/svgComponents/PlusIcon'
import {
  incomingDeliveriesTableColumns,
  storedDeliveriesTableColumns,
} from './columns'
import StoredDeliveriesListDetails from './components/StoredDeliveriesListDetails/StoredDeliveriesListDetails'
import {
  Container,
  PageWrapper,
  SectionCard,
  SectionTitle,
  StyledCardHeader,
  TableContainer,
} from './Shipment.styled'
import useNirResults from './dialogs/useNirResults'
import {
  useDeliveriesService,
  useNirService,
  useWheatCodesService,
} from '../../services'
import {
  defaultFilterParams,
  IFilterParams,
} from '../../types/models/FilterSort'
import { formatFiltersSorts } from '../../components/Table/TableFilter/helpers'
import { useSelector } from 'react-redux'
import { RootState, useSetShowDiscarded, useSetShowStorage } from '../../store'
import { useSystemSettings } from '../../utils/hooks/useSystemSettings'
import { useSampleImporter } from '../../components'

export const Shipment = () => {
  const { checkPermission } = usePermissions()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const showDiscarded = useSelector(
    (state: RootState) => state.shipments.showDiscarded
  )
  const { isEnabled: displayStorage } = useSystemSettings('SHOW_WHEAT_STORAGE')

  const showStorage = useSelector(
    (state: RootState) => state.shipments.showStorage
  )
  const setShowDiscarded = useSetShowDiscarded()
  const setShowStorage = useSetShowStorage()
  const currentCompany = useSelector(
    (state: RootState) => state?.user?.currentCompany
  )

  const { setCurrentPage, setRowsPerPage } = usePagination({
    tableKey: `incoming_deliveries`,
  })

  // TODO: table pagination needs urgent refactoring
  const incomingPaginationState = useSelector(
    (state: any) => state.table?.[`incoming_deliveries_${currentCompany?.id}`]
  )

  const currentPage = incomingPaginationState?.page || 0
  const rowsPerPage = incomingPaginationState?.rowsPerPage || 25

  const {
    setCurrentPage: setStoredCurrentPage,
    setRowsPerPage: setStoredRowsPerPage,
  } = usePagination({ tableKey: 'stored_deliveries' })

  const storedPaginationState = useSelector(
    (state: any) => state.table?.[`stored_deliveries_${currentCompany?.id}`]
  )

  const storedCurrentPage = storedPaginationState?.page || 0
  const storedRowsPerPage = storedPaginationState?.rowsPerPage || 25

  usePageHeader({
    headerTitle: 'Shipments',
  })

  const [renderNirResults, toggleNirResults] = useNirResults()
  const { getWheatCodes } = useWheatCodesService()
  const { getDeliveries } = useDeliveriesService()
  const wheatCodeWithTaskIds = useSelector(
    (state: RootState) => state.recalculateBin.wheatCodeWithTaskIds
  )

  const {
    data: wheatCodesDeliveries,
    isLoading: isWheatCodesDeliveriesLoading,
  } = useQuery({
    queryKey: [
      'wheat_codes_delivery_list',
      storedCurrentPage,
      storedRowsPerPage,
    ],
    queryFn: () =>
      getWheatCodes(null, {
        itemsPerPage: storedRowsPerPage,
        currentPage: storedCurrentPage,
      }),
    retry: false,
    enabled: checkPermission(Entity.DELIVERY, 'get_stored'),
  })

  const { data: wheatCodes } = useQuery({
    queryKey: ['wheat_codes_list'],
    queryFn: () => getWheatCodes(),
    retry: false,
  })

  const wheatCodeOptions = useMemo(() => {
    return wheatCodes?.results?.map((wheatCode) => ({
      label: wheatCode.code,
      value: wheatCode.id?.toString(),
    }))
  }, [wheatCodes?.results])

  const incomingDeliveryColumns = useMemo(
    () => incomingDeliveriesTableColumns(wheatCodeOptions),
    [wheatCodeOptions]
  )

  const storedDeliveryColumns = useMemo(
    () =>
      storedDeliveriesTableColumns(
        wheatCodeWithTaskIds,
        showDiscarded,
        showStorage
      ),
    [wheatCodeWithTaskIds, showDiscarded, showStorage]
  )

  const [filterParams, setFilterParams] =
    useState<IFilterParams>(defaultFilterParams)

  const {
    data: deliveriesData,
    isLoading: isDeliveriesDataLoading,
    refetch: refetchDeliveries,
  } = useQuery(
    ['fetch-deliveries', currentPage, rowsPerPage, filterParams],
    () =>
      getDeliveries(filterParams, {
        currentPage: currentPage,
        itemsPerPage: rowsPerPage,
      }),
    {
      enabled: checkPermission(Entity.DELIVERY, CrudAction.GET),
      refetchOnWindowFocus: false,
    }
  )

  const onDeliveriesPaginationChange = (page: number, perPage: number) => {
    setCurrentPage(page)
    setRowsPerPage(perPage)
  }

  const onStoredDeliveriesPaginationChange = (
    page: number,
    perPage: number
  ) => {
    setStoredCurrentPage(page)
    setStoredRowsPerPage(perPage)
  }

  const { uploadFileAndStream } = useNirService()
  const { handleUpload, fileInputRef } = useSampleImporter({
    uploadFileAndStream,
    onSuccessCallback: refetchDeliveries,
  })

  const isUploading = !!localStorage.getItem(`wheat_nir_job_id`)

  return (
    <Container>
      <PageWrapper
        sx={{
          flexDirection: isSmallScreen ? 'column' : 'row',
        }}
      >
        <SectionCard>
          <StyledCardHeader>
            <SectionTitle>Incoming Deliveries</SectionTitle>
            <Button
              variantType="text"
              startIcon={<PlusIcon color="inherit" />}
              component="label"
              disabled={
                !checkPermission(Entity.DELIVERY, CrudAction.CREATE) ||
                !checkPermission(Entity.NIR, CrudAction.CREATE) ||
                isUploading
              }
            >
              New NIR
              <input
                ref={fileInputRef}
                name="file"
                type="file"
                accept=".dx,.csv"
                onChange={handleUpload}
                hidden
              />
            </Button>
          </StyledCardHeader>
          <TableContainer
            sx={{
              height: isSmallScreen ? 'unset' : 'calc(100vh - 200px)',
            }}
          >
            <Table
              showHeader={true}
              showPagination={true}
              columns={incomingDeliveryColumns}
              data={deliveriesData?.results || []}
              total={deliveriesData?.pagination?.total || 0}
              isLoading={isDeliveriesDataLoading}
              onPaginationChange={onDeliveriesPaginationChange}
              onRowClick={(row) => {
                toggleNirResults(
                  row?.id?.toString(),
                  row?.nir?.nir_number,
                  row?.nir?.arrival_date,
                  row?.wheat_code?.id
                )
              }}
              handleGlobalFilter={(filter, sort) => {
                setFilterParams(formatFiltersSorts(filter, sort))
              }}
              handleGlobalSort={(sort, filter) => {
                setFilterParams(formatFiltersSorts(filter, sort))
              }}
              tableKey="incoming_deliveries"
            />
          </TableContainer>
        </SectionCard>
        <SectionCard>
          <StyledCardHeader>
            <SectionTitle>Stored Deliveries</SectionTitle>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              {displayStorage && (
                <Box>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Show Storage"
                    checked={showStorage}
                    onChange={(_selected, checked) => {
                      setShowStorage(checked)
                    }}
                  />
                </Box>
              )}
              <Box>
                <FormControlLabel
                  control={<Checkbox />}
                  label="Show Discarded"
                  checked={showDiscarded}
                  onChange={(_selected, checked) => {
                    setShowDiscarded(checked)
                  }}
                />
              </Box>
            </Box>
          </StyledCardHeader>
          <TableContainer
            sx={{
              height: isSmallScreen ? 'unset' : 'calc(100vh - 200px)',
            }}
          >
            <Table
              showHeader={false}
              showPagination={true}
              columns={storedDeliveryColumns}
              data={wheatCodesDeliveries?.results || []}
              total={wheatCodesDeliveries?.pagination?.total || 0}
              isLoading={isWheatCodesDeliveriesLoading}
              onPaginationChange={onStoredDeliveriesPaginationChange}
              renderExpandableRow={(row) => (
                <StoredDeliveriesListDetails
                  key={row.id}
                  wheatCodeId={row.id}
                  wheatCode={row.code}
                  showDiscarded={showDiscarded}
                  showStorage={showStorage}
                />
              )}
              showExpandableRowAtRight={true}
              tableKey="stored_deliveries"
            />
          </TableContainer>
        </SectionCard>
      </PageWrapper>
      {renderNirResults}
    </Container>
  )
}
